import { useSelector } from 'react-redux';
import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { selectSessionAdvisor } from '@client/model';
import { useOnboardingNavigation } from '@/features/advisor-onboarding';
import { useAdvisorOnboarding } from '@client/hooks/useAdvisorOnboarding';

type ViewState = 'options' | 'invite-yourself' | 'invite-client' | 'quick-plan';
type SuccessType = 'invited' | 'quick-plan' | null;

const VALID_VIEW_STATES: ViewState[] = [
  'options',
  'invite-yourself',
  'invite-client',
  'quick-plan',
];

function isValidViewState(value: string | null): value is ViewState {
  return value !== null && VALID_VIEW_STATES.includes(value as ViewState);
}

function getSuccessContent(successType: SuccessType) {
  if (successType === 'quick-plan') {
    return {
      title: 'Plan created successfully!',
      description: 'Your client plan is ready. View the results in your dashboard.',
    };
  }
  return {
    title: 'Invitation sent successfully!',
    description:
      'An email has been sent with a link to complete the intake form. You can track progress in your dashboard.',
  };
}

export function useInviteClientStep() {
  const navigate = useNavigate();
  const advisor = useSelector(selectSessionAdvisor);
  const [searchParams, setSearchParams] = useSearchParams();
  const [successType, setSuccessType] = useState<SuccessType>(null);

  const advisorId = advisor?.advisorId ?? '';

  const { markStepAsCompleted, refetch } = useAdvisorOnboarding(advisorId);

  const navigation = useOnboardingNavigation({
    advisorId,
    currentStep: 'first-plan',
    markStepAsCompleted,
  });

  // Derive view state from URL query param
  const viewParam = searchParams.get('view');
  const viewState: ViewState = isValidViewState(viewParam) ? viewParam : 'options';

  const setViewState = useCallback(
    (newView: ViewState) => {
      if (newView === 'options') {
        setSearchParams({}, { replace: false });
      } else {
        setSearchParams({ view: newView }, { replace: false });
      }
    },
    [setSearchParams],
  );

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleInviteSuccess = useCallback(() => {
    setSuccessType('invited');
    refetch();
  }, [refetch]);

  const handleQuickPlanSuccess = useCallback(() => {
    setSuccessType('quick-plan');
    refetch();
  }, [refetch]);

  const handleGoToDashboard = useCallback(() => {
    markStepAsCompleted('first-plan');
    navigation.refreshSessionAndGoToDashboard();
  }, [markStepAsCompleted, navigation]);

  const successContent = getSuccessContent(successType);

  return {
    advisorId,
    viewState,
    successType,
    successContent,
    isPendingDashboardNavigation: navigation.isPendingDashboardNavigation,
    setViewState,
    handleBack,
    handleInviteSuccess,
    handleQuickPlanSuccess,
    handleGoToDashboard,
  };
}
